import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  untracked,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ListStateService } from '@modules/common/components/list/list-state.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { BaseStitchService } from '@modules/common/services/base-stitch.service';
import { BaseStitchListState } from '@modules/common/types/base-stitch-list-state';
import { Stitch } from '@modules/common/types/stitch';
import { StitchFilters } from '@modules/common/types/stitch-filters';
import { Connection } from '@modules/connections/types/connection';
import { Knot } from '@modules/knots/types/knot';
import type { KnotsManageListState } from '@modules/knots/types/knots-manage-list-state';
import { type KPResultsEmptyState } from '@modules/knowledge/components/kp-results-empty/kp-results-empty.component';
import { KP_SELECTION_DRIVEN } from '@modules/knowledge/types/kp-selection-driven';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { KPListType } from '@modules/knowledge/types/kp-list-type';
import { getKPSortLabel } from '@modules/knowledge/types/kp-result-types';
import {
  buildKPResultsFilters,
  kpContextTenantItems,
  kpListSearchConfig,
} from '@modules/knowledge/utils/build-kp-results-filters';
import { ModalService } from '@modules/modal/services/modal.service';
import { HOST_ADVANCED_SEARCH_SERVICE } from '@modules/search/injection-tokens/host-advanced-search-service.injection-token';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateService } from '@modules/settings/services/state.service';
import { StateKey } from '@modules/settings/types/state-key';
import { Tag } from '@modules/tags/types/tag';
import { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { take } from 'rxjs/operators';

@Component({
  template: '',
  standalone: false,
})
export abstract class KnowledgeStitchSectionComponent<S extends Stitch, F extends StitchFilters<S>> implements OnInit {
  public readonly stateKey = input.required<StateKey>();
  public readonly tags = input.required<Tag[]>();
  public readonly knots = input.required<Knot[]>();
  public readonly connections = input.required<Connection[]>();
  public readonly type = input.required<KPListType>();

  public readonly itemClick = output<Stitch>();
  public readonly typeChange = output<KPListType>();
  public readonly filterChange = output<void>();

  public KPListType = KPListType;
  public state: F;
  public knotsSource: KnotSource;

  protected filters: F;
  /** The result count the list reports. */
  protected readonly resultCount = signal(0);
  /** Whether the sort button's menu is open. */
  protected readonly sortMenuOpened = signal(false);
  /** The sort button's label: the list state's sort, as the type's sort menu names it. */
  protected readonly sortLabel = computed(() => getKPSortLabel(this.type(), this.listSortBy()));
  /** The sort button's blue state: its menu is open or a filter is set, and never while it is disabled. */
  protected readonly sortActive = computed(
    () => !this.controlsDisabled() && (this.sortMenuOpened() || this.listFiltersActive()),
  );

  protected abstract readonly listStateService: ListStateService;
  protected abstract readonly stitchService: BaseStitchService<StitchType>;

  protected readonly changeDetector = inject(ChangeDetectorRef);
  // Shared here (was duplicated in every concrete section): the sections read it in handleFilter,
  // and updateFilters() below re-reads it at search time.
  protected readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  protected readonly modalService = inject(ModalService);
  protected readonly kpService = inject(KnowledgePanelService);
  protected readonly stateService = inject(StateService);
  protected readonly injector = inject(Injector);
  protected readonly destroyRef = inject(DestroyRef);
  /** The item open in the 3rd panel. A draft without an id counts as open. */
  private readonly contextItem = toSignal(this.kpService.getContextStitchItem(), { initialValue: null });
  /**
   * The open item as the list's tenant source, so a member workspace's item lists its related rows from
   * that item's owner rather than the viewer's own space. Empty while nothing is open.
   */
  protected readonly contextTenantItems = computed<ReadonlyArray<Stitch>>(() =>
    kpContextTenantItems(this.contextItem()),
  );
  private readonly keywords = toSignal(this.kpService.getKeywords(), { initialValue: [] });
  /** True where the host holds the selection itself (the Atlas panel's Sources view): no open item is needed. Absent in the Knowledge Panel. */
  private readonly selectionDriven = inject(KP_SELECTION_DRIVEN, { optional: true }) === true;
  private readonly active = computed<boolean>(() => this.selectionDriven || !!this.contextItem());
  /** Disables the result type and sort buttons while nothing is open. */
  protected readonly controlsDisabled = computed<boolean>(() => !this.active());
  /** Mirrors `withEmpty` on the request {@link updateFilters} last published: true while none is sent. */
  private readonly requestSkipped = signal(true);
  /** Keyed on the published request, so "No related" answers only a search that ran, open item or not. */
  protected readonly emptyState = computed<KPResultsEmptyState>(() => {
    if (!this.requestSkipped()) {
      return 'none';
    }
    return this.active() ? 'idle' : 'blank';
  });

  // Rows show marker chips for the markers that filtered them.
  protected readonly withTags = computed<boolean>(() => this.tags()?.length > 0);
  protected readonly withKnots = computed<boolean>(() => this.knots()?.length > 0);
  protected readonly withConnections = computed<boolean>(() => this.connections()?.length > 0);

  /**
   * The enclosing advanced-search instance (captured by the KP before it shadows the service —
   * inside the dynamic panel, the DP-scoped one). Full-form modals opened from a section are not
   * part of the KP's isolated search scope, so they get this instance back — see
   * {@link modalInjector}.
   */
  private readonly hostSearchService = inject(HOST_ADVANCED_SEARCH_SERVICE, { optional: true });

  /** The list state's plain fields, re-read on each of its changes. */
  private readonly listSortBy = signal<unknown>(undefined);
  private readonly listFiltersActive = signal(false);

  /** Lazily built by {@link modalInjector}; both of its inputs are constants for this component's lifetime. */
  private modalInjectorCache?: Injector;

  constructor() {
    effect(() => {
      this.updateFilters();
    });

    // Closing the item in the 3rd panel clears this list's sort & filter: the next item starts from the default
    // (Johanna, 2026-10-21: a filter set for one item stayed on after it closed).
    let wasActive = false;
    effect(() => {
      const active = this.active();
      if (wasActive && !active) {
        untracked(() => this.listStateService.state.resetToDefault());
      }
      wasActive = active;
    });
  }

  public ngOnInit(): void {
    this.stateService
      .getState(StateKey.kpKnotsState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: KnotsManageListState) => {
        this.knotsSource = state?.filters?.source;
      });

    this.listStateService
      .value()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.handleFilter(state);
        this.listSortBy.set(state.sortBy);
        this.listFiltersActive.set(state.hasActiveFilters());
      });
  }

  protected abstract handleFilter(listState: BaseStitchListState<S, unknown>): void;

  public handleClickItem(id: string): void {
    this.stitchService
      .getItem(id, kpListSearchConfig(this.type(), this.contextItem()))
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((item) => {
        this.itemClick.emit(item);
      });
  }

  public handleDoubleClickItem(id: string): void {
    this.stitchService
      .getItem(id, kpListSearchConfig(this.type(), this.contextItem()))
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((item) => {
        this.modalService.openFullForm(item, this.modalInjector());
      });
  }

  /**
   * Injector for UI opened OUTSIDE the KP scope (full-form modals). The section's own injector sits
   * below the KP's element providers, so passing it verbatim would leak the KP-scoped
   * {@link AdvancedSearchService} into the modal — its marker-tooltip "Search" would then re-query
   * the KP result list. Re-provide the enclosing instance on top.
   */
  private modalInjector(): Injector {
    if (!this.hostSearchService) {
      return this.injector;
    }
    // Both inputs are constants for this component's lifetime, so one injector serves every modal.
    this.modalInjectorCache ??= Injector.create({
      providers: [{ provide: AdvancedSearchService, useValue: this.hostSearchService }],
      parent: this.injector,
    });
    return this.modalInjectorCache;
  }

  public handleTypeChange(type: KPListType): void {
    this.typeChange.emit(type);
    this.updateFilters();
  }

  protected updateFilters(): void {
    if (!this.filters) {
      return;
    }

    const resultsFilters = buildKPResultsFilters(this.filters, this.type(), {
      tags: this.tags(),
      knots: this.knots(),
      connections: this.connections(),
      search: null,
      keywords: this.keywords(),
      contextItem: this.contextItem(),
      timeZone: this.displayTimeZoneService.timeZone,
    });

    this.state = resultsFilters;
    this.requestSkipped.set(resultsFilters.withEmpty);
    // markForCheck, not detectChanges: updateFilters also runs inside the constructor effect() (a
    // signal flush during change detection), where a synchronous subtree check risks re-entrant CD.
    this.changeDetector.markForCheck();
  }
}
